import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedModule } from '@src/app/shared/shared.module';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import {
  MeetingTimelineDto,
  MeetingTimelineItemDto,
} from '@interfaces/meetings/responses/meeting.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { itensVisiveisDaLinhaDoTempo } from './meeting-timeline-state';

/**
 * Linha do tempo da gravação (card 3244), na aba Detalhes, só para o time interno: o que aconteceu com o bot e
 * com a gravação, em ordem (sala de espera, admitido, gravação começou, outro gravador entrou, bot removido).
 *
 * Por padrão mostra só o que importa (bot, organizador e outros gravadores); as entradas e saídas dos demais
 * participantes ficam atrás de "Ver todas as entradas e saídas". A tradução dos eventos é do backend
 * (`GET meetings/{id}/timeline`); a Recall não informa quem removeu o bot, e a tela nunca afirma quem foi.
 */
@Component({
  selector: 'app-meeting-timeline',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './meeting-timeline.component.html',
  styleUrl: './meeting-timeline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingTimelineComponent implements OnInit {
  readonly meetingId = input.required<string>();

  private readonly http = inject(MeetingsHttpService);
  private readonly datas = inject(DateFormatterService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly linhaDoTempo = signal<MeetingTimelineDto | null>(null);
  protected readonly carregando = signal(true);
  protected readonly erro = signal(false);
  /** Mostra também as entradas e saídas de quem não é organizador nem gravador. */
  protected readonly verTudo = signal(false);

  protected readonly itens = computed(() =>
    itensVisiveisDaLinhaDoTempo(this.linhaDoTempo()?.items, this.verTudo())
  );

  /** Quantas entradas e saídas ficam escondidas no modo resumido. */
  protected readonly escondidos = computed(
    () => (this.linhaDoTempo()?.items ?? []).filter(i => !i.relevant).length
  );

  ngOnInit(): void {
    this.http
      .getTimeline(this.meetingId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: t => {
          this.linhaDoTempo.set(t);
          this.carregando.set(false);
        },
        error: () => {
          this.erro.set(true);
          this.carregando.set(false);
        },
      });
  }

  /** Hora local do evento, com segundos (os eventos do bot acontecem em segundos). */
  protected hora(item: MeetingTimelineItemDto): string {
    const data = this.datas.parseDate(item.at);
    if (!data) return '';
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${dois(data.getHours())}:${dois(data.getMinutes())}:${dois(data.getSeconds())}`;
  }

  /** Ícone pelo tipo do evento. */
  protected icone(item: MeetingTimelineItemDto): string {
    switch (item.kind) {
      case 'RecordingStarted':
        return 'pi-video';
      case 'OtherRecorderJoined':
      case 'OtherRecorderLeft':
        return 'pi-microphone';
      case 'ParticipantJoined':
        return 'pi-sign-in';
      case 'ParticipantLeft':
        return 'pi-sign-out';
      case 'BotRemoved':
      case 'BotNotAdmitted':
      case 'NobodyJoined':
      case 'BotFailed':
        return 'pi-times-circle';
      case 'BotWaiting':
        return 'pi-clock';
      default:
        return 'pi-circle';
    }
  }
}
